import React from 'react';
import { graphql, useFragment } from 'react-relay';
import Lozenge from '@atlaskit/lozenge';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import type { NavigationItemTab_tab$key } from '@atlassian/jira-relay/src/__generated__/NavigationItemTab_tab.graphql';
import type { NavigationItemTab_container$key } from '@atlassian/jira-relay/src/__generated__/NavigationItemTab_container.graphql';
import type { TabsDescriptor } from '@atlassian/jira-horizontal-nav-common/src/common/types.tsx';
import { DefaultTab } from './DefaultTab.tsx';
import { AppTab } from './AppTab.tsx';
import { ShortcutsTab } from './shortcuts-tab/ShortcutsTab.tsx';
import messages from './messages.tsx';

type Props = {
	tabFragment: NavigationItemTab_tab$key;
	containerFragment: NavigationItemTab_container$key;
	position: number;
	beforeItemId: string | null;
	afterItemId: string | null;
	tabsDescriptor: TabsDescriptor;
	isRenaming: boolean;
	onRenamingChange: (isRenaming: boolean) => void;
	shouldHighlight?: boolean;
	onDisableHighlight?: (tabKey: string) => void;
};

export const NavigationItemTab = ({
	tabFragment,
	containerFragment,
	position,
	beforeItemId,
	afterItemId,
	tabsDescriptor,
	isRenaming,
	onRenamingChange,
	shouldHighlight,
	onDisableHighlight,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const tabData = useFragment(
		graphql`
			fragment NavigationItemTab_tab on JiraNavigationItem {
				__typename
				typeKey @required(action: THROW)
				url
				...DefaultTab_tab
				...ShortcutsTab_tab
				...AppTab_tab
			}
		`,
		tabFragment,
	);
	const containerData = useFragment(
		graphql`
			fragment NavigationItemTab_container on JiraContainerNavigationResult {
				...DefaultTab_container
				...ShortcutsTab_container
				...AppTab_container
			}
		`,
		containerFragment,
	);

	if (tabData.__typename === 'JiraAppNavigationItem') {
		return (
			<AppTab
				tabFragment={tabData}
				containerFragment={containerData}
				position={position}
				beforeItemId={beforeItemId}
				afterItemId={afterItemId}
			/>
		);
	}

	if (tabData.typeKey === 'SHORTCUTS') {
		return (
			<ShortcutsTab
				tabFragment={tabData}
				containerFragment={containerData}
				position={position}
				beforeItemId={beforeItemId}
				afterItemId={afterItemId}
				isRenaming={isRenaming}
				onRenamingChange={onRenamingChange}
			/>
		);
	}

	if (tabData.url == null) {
		return null;
	}

	const Icon = tabsDescriptor[tabData.typeKey]?.Icon ?? null;

	// when clean up the add_capacity_tab_to_the_board_horizontal_nav, please check if need to keep the EAP lozenge, if not, please remove the whole if block
	if (tabData.typeKey === 'CAPACITY' && fg('add_capacity_tab_to_the_board_horizontal_nav')) {
		return (
			<DefaultTab
				tabFragment={tabData}
				containerFragment={containerData}
				position={position}
				icon={Icon && <Icon label="" />}
				beforeItemId={beforeItemId}
				afterItemId={afterItemId}
				isRenaming={isRenaming}
				elemAfter={<Lozenge appearance="new">{formatMessage(messages.eapLozenge)}</Lozenge>}
				onRenamingChange={onRenamingChange}
				shouldHighlight={shouldHighlight}
				onDisableHighlight={onDisableHighlight}
			/>
		);
	}

	return (
		<DefaultTab
			tabFragment={tabData}
			containerFragment={containerData}
			position={position}
			icon={Icon && <Icon label="" />}
			beforeItemId={beforeItemId}
			afterItemId={afterItemId}
			isRenaming={isRenaming}
			onRenamingChange={onRenamingChange}
			shouldHighlight={shouldHighlight}
			onDisableHighlight={onDisableHighlight}
		/>
	);
};
